import type React from 'react';

/**
 * Props for the TranscriptPanel component
 */
export interface TranscriptPanelProps {
  /** Whether the transcript panel is currently open */
  isOpen: boolean;
  /** Function to call when the panel should be closed */
  onClose: () => void;
  /** Additional CSS class names */
  className?: string;
  /** Test identifier for automated testing */
  'data-testid'?: string;
}

/**
 * Meeting Transcription Panel Component
 *
 * A dark-themed side panel that displays meeting transcription with download and close functionality.
 * Designed to match the Sangoma Meet interface with proper accessibility support.
 */
export const TranscriptPanel: React.FC<TranscriptPanelProps> = ({
  isOpen,
  onClose,
  className = '',
  'data-testid': dataTestId,
}) => {
  const handleDownload = () => {
    // TODO: Implement transcript download functionality
  };

  return (
    <div
      className={`transcript-panel ${isOpen ? 'transcript-panel-open' : ''} ${className}`}
      data-testid={dataTestId}
    >
      <div className="transcript-panel-header">
        <h2 className="transcript-panel-title">Meeting Transcription</h2>
        <div className="transcript-panel-actions">
          <button
            type="button"
            className="transcript-panel-download"
            onClick={handleDownload}
            aria-label="Download transcript"
            data-testid="transcript-download-button"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <polyline
                points="7,10 12,15 17,10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <line
                x1="12"
                y1="15"
                x2="12"
                y2="3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            type="button"
            className="transcript-panel-close"
            onClick={onClose}
            aria-label="Close transcript panel"
            data-testid="transcript-close-button"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className="transcript-panel-content">
        <div className="transcript-placeholder">
          <div className="transcript-placeholder-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" fill="currentColor" />
              <path
                d="M19 10v2a7 7 0 0 1-14 0v-2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 19v4M8 23h8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h3 className="transcript-placeholder-title">Transcription Ready</h3>
          <p className="transcript-placeholder-description">
            Meeting transcription will appear here in real-time once the feature is enabled.
          </p>
        </div>
      </div>
    </div>
  );
};
